import { useEffect, useMemo, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Input from "@/v2-ui/input"
import Switch from "@/v2-ui/switch"
import Select from "@/v2-ui/select"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import { useUptimeConfigProbeUpsertMutation } from "@/v2-backoffice-client/uptime/configProbe/upsert/uptime.configProbe.upsert.mutation"
import type { UptimeConfigProbeListQuery } from "@/v2-backoffice-client/uptime/configProbe/list/__types__/uptime.configProbe.list.query"

type Probe = UptimeConfigProbeListQuery["getUptimeProbesList"]["items"][number]

export type UptimeConfigProbeDialogProps = DialogProps & {
  mode: "create" | "edit"
  probe?: Probe | null
}

const FORM_ID = "uptime-config-probe-form"

// External-category vocabulary mirrors `external.runner.go` so the
// dialog only ever produces values the SQL CHECK constraint
// accepts. Empty maps to NULL on submit (the runner treats that as
// the default `api` category).
const EXTERNAL_CATEGORY_OPTIONS = [
  { label: "(api / default)", value: "" },
  { label: "deps", value: "deps" },
  { label: "cloud", value: "cloud" },
] as const

const EXTERNAL_PLATFORM_OPTIONS = [
  { label: "Placepoint v2", value: "placepoint-v2" },
  { label: "Placepoint v1", value: "placepoint-v1" },
  { label: "Propcloud", value: "propcloud" },
  { label: "Newsecmaps", value: "newsecmaps" },
] as const

function makeInitial(probe: Probe | null | undefined) {
  return {
    source: probe?.Source ?? "app",
    identifier: probe?.Identifier ?? "",
    kind: (probe?.Kind ?? "app") as "app" | "external",
    name: probe?.Name ?? "",
    platform: probe?.Platform ?? "placepoint-v2",
    category: probe?.Category ?? "",
    hsPath: probe?.HsPath ?? "",
    prodName: probe?.ProdName ?? "",
    url: probe?.Url ?? "",
    protocol: probe?.Protocol ?? "",
    isEnabled: probe?.IsEnabled ?? true,
  }
}

// UptimeConfigProbeDialog covers both create and edit. The Kind
// switcher reshuffles which fields are required (per the SQL
// CHECK constraints in `Backoffice_UptimeProbes.sql`):
//   - Kind = 'app'      -> HsPath required, no Url / Platform / Category.
//   - Kind = 'external' -> Url + Platform required, Category optional
//                          (api / deps / cloud).
function UptimeConfigProbeDialog(props: UptimeConfigProbeDialogProps) {
  const { mode, probe, open, onOpenChange, ...dialogProps } = props

  const [form, setForm] = useState(() => makeInitial(probe))
  const [upsert, { loading, error, reset }] =
    useUptimeConfigProbeUpsertMutation({
      onCompleted() {
        onOpenChange?.(false)
      },
    })

  useEffect(() => {
    if (open) {
      setForm(makeInitial(probe))
      reset()
    }
  }, [open, probe, reset])

  // Derive the runtime Source value the SQL row stores.
  // Kind='app' rows always store the literal `'app'`; Kind='external'
  // rows store `external.<platform>[.<category>]`. Mirrors
  // external.SourceFor in v2-uptime-monitor so the runner's view of
  // the row matches whatever the operator just typed.
  const derivedSource = useMemo(() => {
    if (form.kind === "app") return "app"
    if (!form.platform) return form.source
    if (!form.category || form.category === "api") {
      return `external.${form.platform}`
    }
    return `external.${form.platform}.${form.category}`
  }, [form.kind, form.platform, form.category, form.source])

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {mode === "edit" ? "Edit probe" : "New probe"}
          </Dialog.Title>
          <Dialog.Description>
            Editorial probe definition. v2-uptime-monitor reads enabled rows on
            every run; the JSON manifest in code is the fallback only.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-3" error={error} />

        <form
          id={FORM_ID}
          className="uptime-config-form"
          onSubmit={(e) => {
            e.preventDefault()
            upsert({
              variables: {
                input: {
                  source: derivedSource,
                  identifier: form.identifier.trim(),
                  kind: form.kind,
                  name: form.name.trim() === "" ? null : form.name.trim(),
                  platform:
                    form.kind === "external" && form.platform
                      ? form.platform
                      : null,
                  category:
                    form.kind === "external" && form.category
                      ? form.category
                      : null,
                  hsPath:
                    form.kind === "app" && form.hsPath ? form.hsPath : null,
                  prodName:
                    form.kind === "app" && form.prodName ? form.prodName : null,
                  url: form.kind === "external" && form.url ? form.url : null,
                  protocol:
                    form.protocol.trim() === "" ? null : form.protocol.trim(),
                  isEnabled: form.isEnabled,
                },
              },
            })
          }}
        >
          <div className="uptime-config-form-row">
            <div className="uptime-config-form-field">
              <Label htmlFor="uptime-config-probe-kind">Kind</Label>
              <Select
                value={form.kind}
                onValueChange={(v) => {
                  setForm({ ...form, kind: v as "app" | "external" })
                }}
                disabled={mode === "edit"}
              >
                <Select.Trigger id="uptime-config-probe-kind">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="app">App (Placepoint app)</Select.Item>
                  <Select.Item value="external">
                    External (partner / cloud / dependency)
                  </Select.Item>
                </Select.Content>
              </Select>
            </div>

            <div className="uptime-config-form-field">
              <Label htmlFor="uptime-config-probe-identifier">Identifier</Label>
              <Input
                id="uptime-config-probe-identifier"
                type="text"
                required
                autoFocus={mode === "create"}
                disabled={mode === "edit"}
                placeholder={
                  form.kind === "app"
                    ? "e.g. v2-backoffice-api"
                    : "e.g. plot-report"
                }
                value={form.identifier}
                onChange={(e) => {
                  setForm({ ...form, identifier: e.target.value })
                }}
              />
            </div>
          </div>

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-probe-name">Display name</Label>
            <Input
              id="uptime-config-probe-name"
              type="text"
              placeholder="Optional human-readable label"
              value={form.name}
              onChange={(e) => {
                setForm({ ...form, name: e.target.value })
              }}
            />
          </div>

          {form.kind === "app" && (
            <div className="uptime-config-form-row">
              <div className="uptime-config-form-field">
                <Label htmlFor="uptime-config-probe-hs">Health path</Label>
                <Input
                  id="uptime-config-probe-hs"
                  type="text"
                  required
                  placeholder="/__hs"
                  value={form.hsPath}
                  onChange={(e) => {
                    setForm({ ...form, hsPath: e.target.value })
                  }}
                />
                <p className="uptime-config-form-hint">
                  Path appended after the deploy slot host (`/__hs` for most
                  apps, `/` for Functions and the static client).
                </p>
              </div>
              <div className="uptime-config-form-field">
                <Label htmlFor="uptime-config-probe-prod">Prod app name</Label>
                <Input
                  id="uptime-config-probe-prod"
                  type="text"
                  placeholder="Optional"
                  value={form.prodName}
                  onChange={(e) => {
                    setForm({ ...form, prodName: e.target.value })
                  }}
                />
                <p className="uptime-config-form-hint">
                  Azure App Service name when it deviates from the identifier
                  (e.g. legacy apps).
                </p>
              </div>
            </div>
          )}

          {form.kind === "external" && (
            <>
              <div className="uptime-config-form-row">
                <div className="uptime-config-form-field">
                  <Label htmlFor="uptime-config-probe-platform">Platform</Label>
                  <Select
                    value={form.platform}
                    onValueChange={(v) => {
                      setForm({ ...form, platform: v })
                    }}
                  >
                    <Select.Trigger id="uptime-config-probe-platform">
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      {EXTERNAL_PLATFORM_OPTIONS.map((opt) => (
                        <Select.Item key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                </div>
                <div className="uptime-config-form-field">
                  <Label htmlFor="uptime-config-probe-category">Category</Label>
                  <Select
                    value={form.category || "__api__"}
                    onValueChange={(v) => {
                      setForm({
                        ...form,
                        category: v === "__api__" ? "" : v,
                      })
                    }}
                  >
                    <Select.Trigger id="uptime-config-probe-category">
                      <Select.Value />
                    </Select.Trigger>
                    <Select.Content>
                      {EXTERNAL_CATEGORY_OPTIONS.map((opt) => (
                        <Select.Item
                          key={opt.value || "__api__"}
                          value={opt.value || "__api__"}
                        >
                          {opt.label}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                </div>
              </div>

              <div className="uptime-config-form-field">
                <Label htmlFor="uptime-config-probe-url">URL</Label>
                <Input
                  id="uptime-config-probe-url"
                  type="url"
                  required
                  placeholder="https://example.com/health"
                  value={form.url}
                  onChange={(e) => {
                    setForm({ ...form, url: e.target.value })
                  }}
                />
              </div>
            </>
          )}

          <div className="uptime-config-form-field">
            <Label htmlFor="uptime-config-probe-protocol">Protocol</Label>
            <Input
              id="uptime-config-probe-protocol"
              type="text"
              placeholder="Optional probe protocol override"
              value={form.protocol}
              onChange={(e) => {
                setForm({ ...form, protocol: e.target.value })
              }}
            />
            <p className="uptime-config-form-hint">
              Leave blank to use the runner default. Set when the endpoint needs
              `wms` / `arcgis.map` / `wmts` / etc.
            </p>
          </div>

          <Label
            className="uptime-config-form-toggle"
            htmlFor="uptime-config-probe-enabled"
          >
            <span>Enabled</span>
            <Switch
              id="uptime-config-probe-enabled"
              checked={form.isEnabled}
              onCheckedChange={(checked) => {
                setForm({ ...form, isEnabled: checked === true })
              }}
            />
          </Label>

          <p className="uptime-config-form-hint">
            Source on row: <code>{derivedSource}</code>
          </p>
        </form>

        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} isLoading={loading}>
            {mode === "edit" ? "Save" : "Create"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default UptimeConfigProbeDialog
